import type { IConfigCache, IConfigCacheAssets, IConfigCacheOAuthToken, ILog } from '@monofe/util';
import type { IMessageInstantiator } from '../../message/interface/instantiator';
import type { IRegexSerializer } from '../../util';
import bridge from './dispatch';

function _isConfigToken(object: unknown): object is IConfigCacheOAuthToken {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IConfigCacheOAuthToken> = object as Partial<IConfigCacheOAuthToken>;

    return !isNullOrUndefined(record.tokenPath);
}

function _isConfigAsset(object: unknown): object is IConfigCacheAssets {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IConfigCacheAssets> = object as Partial<IConfigCacheAssets>;

    return (
        !isNullOrUndefined(record.cacheName) &&
        !isNullOrUndefined(record.expiration) &&
        !isNullOrUndefined(record.maxEntries) &&
        !isNullOrUndefined(record.matcher)
    );
}

function _tokenConfigs(config: IConfigCache): IConfigCacheOAuthToken[] {
    const arr: IConfigCacheOAuthToken[] = [];

    for (const settings of Object.values(config)) {
        if (!_isConfigToken(settings)) {
            continue;
        }
        arr.push(settings);
    }

    return arr;
}

function _assetConfigs(config: IConfigCache): IConfigCacheAssets[] {
    const arr: IConfigCacheAssets[] = [];

    for (const settings of Object.values(config)) {
        if (!_isConfigAsset(settings)) {
            continue;
        }
        arr.push(settings);
    }

    return arr;
}

function setupSWRules(
    log: ILog,
    regexParser: IRegexSerializer,
    message: IMessageInstantiator,
    worker: ServiceWorker,
    config: IConfigCache,
): void {
    log.info('SW-Rules, Adding Cache First');

    const deps = {
        log,
        regexParser,
        message,
        worker,
        config,
    };

    for (const settings of _assetConfigs(config)) {
        log.info(
            `SW-Rules, Registering cache-first: ${settings.cacheName}, ${JSON.stringify(settings)}`,
        );
        bridge.addCacheFirst(
            deps,
            /* Matcher: */ settings.matcher,
            /* MaxAge: */ settings.expiration,
            /* MaxEntries: */ settings.maxEntries,
            /* CacheName */ settings.cacheName,
            /* Ignore-search */ settings.cacheQueryOptions ?? {},
            /* Fetch overrides */ settings.fetchOptions ?? {},
        );
    }

    for (const settings of _tokenConfigs(config)) {
        log.info(`SW-Rules, Adding OAuth Token Cache: %{settings.tokenPath}`);
        bridge.addOAuthTokenCache(deps, settings.tokenPath);
    }
}

export default Object.freeze(setupSWRules);
